import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { mutationOptions, QueryClient, queryOptions } from "@tanstack/angular-query-experimental";
import { from, lastValueFrom, map, Observable, switchMap } from "rxjs";

import { AWSHttpClient } from "@intocare/aws";
import { Environment, ENVIRONMENT_TOKEN } from "@intocare/environments";
import { extractETagHeaderValue, getStrongIfMatchHeader, I18n, I18nMap, SupportedLocale } from "@intocare/util";

import {
	Address,
	DACreateOrganisingAuthority,
	DAOrganisingAuthority,
	DASupportInfo,
	FederatedEntity,
} from "../../shared/organisation/organisation";

export const configurationQueryKeys = {
	base() {
		return ["organisation"] as const;
	},
	hqOrganisingAuthorities() {
		return [...this.base(), "organising-authorities"] as const;
	},
	baseSiteById(params: { siteId: string }) {
		return [...this.base(), "site", params.siteId] as const;
	},
	siteConfiguration(params: { siteId: string }) {
		return [...this.baseSiteById(params), "configuration"] as const;
	},
	supportInfo(params: { siteId: string }) {
		return [...this.baseSiteById(params), "support-info"] as const;
	},
	baseCareFacilities(params: { siteId: string }) {
		return [...this.baseSiteById(params), "care-facilities"] as const;
	},
	careFacilities(params: { siteId: string }) {
		return [...this.baseCareFacilities(params), "list"] as const;
	},
	baseCareFacilityDetails(params: { siteId: string; careFacilityId: string }) {
		return [...this.baseCareFacilities(params), params.careFacilityId, "detail"] as const;
	},
	careFacilityById(params: { siteId: string; careFacilityId: string }) {
		return [...this.baseCareFacilityDetails(params), "info"] as const;
	},
	careFacilityCertificateStatus(params: { siteId: string; careFacilityId: string }) {
		return [...this.baseCareFacilityDetails(params), "certificate-status"] as const;
	},
	newCareFacilityAvailableTypes(params: { siteId: string }) {
		return [...this.baseSiteById(params), "new-care-facility-available-types"] as const;
	},
	departmentsWithAtLeastOneRoom(params: { siteId: string }) {
		return [...this.baseSiteById(params), "departments-with-at-least-one-room"] as const;
	},
};

export const configurationMutationKeys = {
	base() {
		return ["organisation"] as const;
	},
	updateSiteConfiguration(params: { siteId: string }) {
		return [...this.base(), params.siteId, "update-site-configuration"] as const;
	},
	updateCareFacility(params: { siteId: string; careFacilityId: string }) {
		return [...this.base(), params.siteId, "update-care-facility", params.careFacilityId] as const;
	},
	createOrganisingAuthority() {
		return [...this.base(), "create-organising-authority"] as const;
	},
};

interface BaseCareFacility {
	id: string;
	name: I18n;
	label?: I18n;
	responsible: {
		firstName: string;
		name: string;
		phone: string;
		email: string;
	};
	numberOfBeds: number;
	siteId: string;
	campusId?: string;
	ehealth?: {
		expirationDate: string;
		actorId: string;
		applicationId: string;
		nihii: string;
		version: number;
	};
}

interface RVTCareFacility extends BaseCareFacility {
	type: CareFacilityType.RVT;
	nihii: string;
}

interface ROBCareFacility extends BaseCareFacility {
	type: CareFacilityType.ROB;
	nihii: string;
	hco: string;
}

interface AWCareFacility extends BaseCareFacility {
	type: CareFacilityType.AW;
}

interface CVKCareFacility extends BaseCareFacility {
	type: CareFacilityType.CVK;
	nihii: string;
	hco: string;
}

interface CVHCareFacility extends BaseCareFacility {
	type: CareFacilityType.CVH;
	hco: string;
}

interface DVCCareFacility extends BaseCareFacility {
	type: CareFacilityType.DVC;
	nihii: string;
	hco: string;
}

interface LDCCareFacility extends BaseCareFacility {
	type: CareFacilityType.LDC;
}

type DAInternalCareFacility =
	| RVTCareFacility
	| ROBCareFacility
	| AWCareFacility
	| CVKCareFacility
	| CVHCareFacility
	| DVCCareFacility
	| LDCCareFacility;

interface DAInternalSiteConfiguration {
	siteId: number;
	home: string;
	trigram: string;
	supportedLocales: SupportedLocale[];
	supportInfo: I18nMap<DASupportInfo>;
	name: I18n;
	federatedEntity: FederatedEntity;
	address: Address;
	numberOfBeds: number;
	organisingAuthority: DAOrganisingAuthority;
}

export interface DASiteConfiguration extends DAInternalSiteConfiguration {
	version: string;
}
export type DACareFacility = {
	version: number | string;
} & DAInternalCareFacility;

export type DACareFacilitySummary = DAInternalCareFacility & {
	version: number | string;
	name: string;
};

interface DARoom {
	id: string;
	name: string;
	version: number;
}

export interface DADepartmentWithRooms {
	id: string;
	name: I18n;
	display: string;
	rooms: DARoom[];
	version: number;
}

export enum CareFacilityType {
	RVT = "RVT",
	ROB = "ROB",
	AW = "AW",
	CVK = "CVK",
	CVH = "CVH",
	DVC = "DVC",
	LDC = "LDC",
}

interface DANewCareFacilityAvailableType {
	availableCareFacilityTypes: { code: CareFacilityType }[];
}

export interface DADownloadBelRAIDocumentBody {
	contact: {
		email: string;
		phone: string;
		name: string;
	};
	integration: {
		hco: string;
		id: CareFacilityType;
		organisationName: string;
		kbo: string;
	};
}

export interface DAUpdateSiteConfigurationBody {
	supportInfo: Partial<I18nMap<DASupportInfo>>;
	name: I18n;
	address: Address;
	version: string | null;
	organisingAuthorityId: string;
}

export interface DAUploadKeystore {
	careFacilityId: string;
	password: string;
	file: File;
	version?: number;
}

export type DAEditCareFacilityBody = Omit<DAInternalCareFacility, "siteId" | "hco" | "name"> & {
	version: string | number;
};

export type DACreateCareFacilityBody = Omit<DAInternalCareFacility, "id" | "siteId" | "name">;

export interface DAMySiteBody {
	id: string;
	home: string;
	name: I18n;
	address: Address;
	supportedLocales: SupportedLocale[];
	organisingAuthority: {
		kbo: string;
	};
}

enum CertificateStatus {
	OK = "OK",
	WARNING = "WARNING",
	ERROR = "ERROR",
}

export interface DACertificateStatus {
	status: CertificateStatus;
	message?: string;
}

export interface DAUploadKeystoreResponse {
	code: string;
	message: string;
}

const arrayBufferToBase64 = (buffer: ArrayBufferLike) => {
	const bytes = new Uint8Array(buffer);

	return window.btoa(String.fromCharCode(...bytes));
};

@Injectable({
	providedIn: "root",
})
export class ConfigurationService {
	private readonly httpClient = inject(HttpClient);
	private readonly awsHttpClient = inject(AWSHttpClient);
	private readonly queryClient = inject(QueryClient);
	private environment = inject<Environment>(ENVIRONMENT_TOKEN);

	private getSiteConfiguration(siteId: string): Observable<DASiteConfiguration> {
		return this.httpClient
			.get<DAInternalSiteConfiguration>(
				`${this.environment.API.public}/organisation/portal/sites/${siteId}/site-configuration`,
				{
					observe: "response",
				},
			)
			.pipe(
				map((response) => {
					const eTag = response.headers.get("etag");
					const version = extractETagHeaderValue(eTag);

					if (!response.body) {
						throw new Error("No body found in response");
					}

					if (!version) {
						throw new Error("No version found in response");
					}

					return {
						version,
						...response.body,
					};
				}),
			);
	}

	getSiteConfigurationQueryOptions = (siteId: string) => {
		return queryOptions({
			queryKey: configurationQueryKeys.siteConfiguration({ siteId }),
			queryFn: () => lastValueFrom(this.getSiteConfiguration(siteId)),
			enabled: !!siteId,
		});
	};

	private getDepartmentsWithAtLeastOneRoom(siteId: string): Observable<DADepartmentWithRooms[]> {
		return this.httpClient
			.get<{
				departments: DADepartmentWithRooms[];
			}>(`${this.environment.API.public}/organisation/portal/departments-with-at-least-one-room`, {
				params: {
					site_id: siteId,
				},
			})
			.pipe(map((data) => data.departments));
	}

	getDepartmentsWithAtLeastOneRoomQueryOptions = (siteId: string) => {
		return queryOptions({
			queryKey: configurationQueryKeys.departmentsWithAtLeastOneRoom({ siteId }),
			queryFn: () => lastValueFrom(this.getDepartmentsWithAtLeastOneRoom(siteId)),
			enabled: !!siteId,
		});
	};

	private getCareFacilities(siteId: string): Observable<DACareFacilitySummary[]> {
		return this.httpClient
			.get<{
				careFacilities: DACareFacilitySummary[];
			}>(`${this.environment.API.public}/organisation/portal/sites/${siteId}/care-facilities`)
			.pipe(map((data) => data.careFacilities));
	}

	getCareFacilitiesQueryOptions = (siteId: string) => {
		return queryOptions({
			queryKey: configurationQueryKeys.careFacilities({ siteId }),
			queryFn: () => lastValueFrom(this.getCareFacilities(siteId)),
			enabled: !!siteId,
		});
	};

	private getCareFacilityById(siteId: string, careFacilityId: string): Observable<DACareFacility> {
		return this.httpClient
			.get<DAInternalCareFacility>(`${this.environment.API.public}/organisation/portal/sites/${siteId}/care-facility`, {
				params: {
					careFacilityId,
				},
				observe: "response",
			})
			.pipe(
				map((response) => {
					const eTag = response.headers.get("etag");
					const version = extractETagHeaderValue(eTag);

					if (!response.body) {
						throw new Error("No body found in response");
					}

					if (!version) {
						throw new Error("No version found in response");
					}

					return {
						version,
						...response.body,
					};
				}),
			);
	}

	getCareFacilityByIdQueryOptions = (siteId: string, careFacilityId: string) => {
		return queryOptions({
			queryKey: configurationQueryKeys.careFacilityById({ siteId, careFacilityId }),
			queryFn: () => lastValueFrom(this.getCareFacilityById(siteId, careFacilityId)),
			enabled: !!siteId && !!careFacilityId,
		});
	};

	private getNewCareFacilityAvailableTypes(siteId: string) {
		return this.httpClient
			.get<DANewCareFacilityAvailableType>(
				`${this.environment.API.public}/organisation/portal/sites/${siteId}/new-care-facility-available-types`,
			)
			.pipe(map((data) => data.availableCareFacilityTypes));
	}

	getNewCareFacilityAvailableTypesQueryOptions = (siteId: string) => {
		return queryOptions({
			queryKey: configurationQueryKeys.newCareFacilityAvailableTypes({ siteId }),
			queryFn: () => lastValueFrom(this.getNewCareFacilityAvailableTypes(siteId)),
			enabled: !!siteId,
		});
	};

	updateCareFacility(siteId: string, careFacility: DAEditCareFacilityBody) {
		const { version, ...updateCareFacilityBody } = careFacility;
		const headers = new HttpHeaders(getStrongIfMatchHeader(version));

		return this.httpClient.post<void>(
			`${this.environment.API.public}/organisation/portal/sites/${siteId}/update-care-facility`,
			updateCareFacilityBody,
			{ headers },
		);
	}

	createCareFacility(siteId: string, body: DACreateCareFacilityBody) {
		return this.httpClient.post<void>(
			`${this.environment.API.public}/organisation/portal/sites/${siteId}/create-care-facility`,
			body,
		);
	}

	testBelRAIConnection(careFacilityType: CareFacilityType) {
		const legacyCareFacilityType = this.getLegacyCareFacilityType(careFacilityType);
		return this.awsHttpClient.get<{ connected: boolean }>(`belrai/connection/${legacyCareFacilityType}`);
	}

	createDocumentDownloadUrl(body: DADownloadBelRAIDocumentBody) {
		const legacyCareFacilityType = this.getLegacyCareFacilityType(body.integration.id);
		const transformedBody = {
			...body,
			integration: {
				...body.integration,
				id: legacyCareFacilityType,
			},
		};
		return this.awsHttpClient.post(`belrai/integration-docx-generation`, transformedBody, { responseType: "text" });
	}

	private getLegacyCareFacilityType(careFacilityType: CareFacilityType) {
		switch (careFacilityType) {
			case CareFacilityType.CVK:
				return "cvk";
			case CareFacilityType.ROB:
				return "wzc";
			default:
				return undefined;
		}
	}

	updateSiteConfiguration(siteId: string, body: DAUpdateSiteConfigurationBody) {
		const { version, ...updateSiteConfigurationBody } = body;

		const headers = typeof version === "string" ? new HttpHeaders(getStrongIfMatchHeader(version)) : undefined;

		return this.httpClient.post<void>(
			`${this.environment.API.public}/organisation/portal/sites/${siteId}/update-site-configuration`,
			updateSiteConfigurationBody,
			{ headers },
		);
	}

	uploadEhealthKeystore(siteId: string, data: DAUploadKeystore): Observable<DAUploadKeystoreResponse | undefined> {
		const { careFacilityId, file, password, version } = data;

		const fileToBase64 = (file: File): Promise<string> => {
			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => resolve(arrayBufferToBase64(reader.result as ArrayBuffer));
				reader.onerror = reject;
				reader.readAsArrayBuffer(file);
			});
		};

		return from(fileToBase64(file)).pipe(
			switchMap((base64Keystore) =>
				this.httpClient.post<DAUploadKeystoreResponse | undefined>(
					`${this.environment.API.public}/organisation/portal/sites/${siteId}/upload-ehealth-keystore`,
					{
						careFacilityId,
						keystore: base64Keystore,
						password,
						...(typeof version === "number" ? { version } : {}),
					},
				),
			),
		);
	}

	getOrganisingAuthoritiesQueryOptions = () => {
		return queryOptions({
			queryKey: configurationQueryKeys.hqOrganisingAuthorities(),
			queryFn: () => lastValueFrom(this.getOrganisingAuthorities()),
		});
	};

	private getOrganisingAuthorities(): Observable<DAOrganisingAuthority[]> {
		return this.httpClient
			.get<{
				organisingAuthorities: DAOrganisingAuthority[];
			}>(`${this.environment.API.public}/organisation/portal/organising-authorities`)
			.pipe(map((data) => data.organisingAuthorities));
	}

	createOrganisingAuthority(body: DACreateOrganisingAuthority) {
		return this.httpClient.post<void>(
			`${this.environment.API.public}/organisation/portal/create-organising-authority`,
			body,
		);
	}

	getCertificateStatusQueryOptions = (siteId: string, careFacilityId: string, hasEhealth: boolean) => {
		return queryOptions({
			queryKey: configurationQueryKeys.careFacilityCertificateStatus({ siteId, careFacilityId }),
			queryFn: () => lastValueFrom(this.getCertificateStatus(siteId, careFacilityId)),
			enabled: !!siteId && !!careFacilityId && hasEhealth,
		});
	};

	private getCertificateStatus(siteId: string, careFacilityId: string): Observable<DACertificateStatus> {
		return this.httpClient.get<DACertificateStatus>(
			`${this.environment.API.public}/organisation/portal/sites/${siteId}/ehealth-certificate-status`,
			{
				params: {
					careFacilityId,
				},
			},
		);
	}

	getUploadEHealthKeystoreMutationOptions = (params: { siteId: string; careFacilityId: string; version?: number }) => {
		return mutationOptions({
			mutationFn: async (data: { password: string; file: File }) =>
				lastValueFrom(
					this.uploadEhealthKeystore(params.siteId, {
						careFacilityId: params.careFacilityId,
						file: data.file,
						password: data.password,
						version: params.version,
					}),
				),
			onSuccess: (result) => {
				if (!result?.code) {
					void this.queryClient.invalidateQueries({
						queryKey: configurationQueryKeys.baseCareFacilityDetails({
							siteId: params.siteId,
							careFacilityId: params.careFacilityId,
						}),
					});
				}
			},
			throwOnError: false,
		});
	};
}
